import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X as XIcon, Layers, Terminal, Loader2, Rocket, KeyRound } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

// Client-side convenience mirror of the backend's SECRET_NAME_RE — the server re-validates.
const SECRET_NAME_RE = /^[A-Z][A-Z0-9_]{0,63}$/;

function timeAgo(iso) {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (!isFinite(s)) return "";
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function ProjectSettings({ project, onClose, onSaved }) {
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState(project?.layout?.template || "flat");
  const [previewCommand, setPreviewCommand] = useState(project?.deploy?.preview_command || "");
  const [saving, setSaving] = useState(false);
  // Deploy config (backend: GET/POST /projects/{pid}/deploy-config). The CF token and
  // account id are write-only: never returned by the GET, never echoed after save.
  const [provider, setProvider] = useState(project?.deploy?.provider || "local");
  const [cloudflareApiToken, setCloudflareApiToken] = useState("");
  const [cloudflareAccountId, setCloudflareAccountId] = useState("");
  const [cfProjectName, setCfProjectName] = useState("");
  const [cfCustomDomain, setCfCustomDomain] = useState("");
  const [buildDir, setBuildDir] = useState(""); // pass-through so Save doesn't wipe a stored build_dir
  const [cloudflareConfigured, setCloudflareConfigured] = useState(false);
  const [deploys, setDeploys] = useState([]);
  // Secrets (backend: GET/PUT/DELETE /projects/{pid}/secrets[/{name}]). Names only — values
  // are write-only: PUT once, encrypted at rest, never returned or echoed after save.
  const [secretNames, setSecretNames] = useState([]);
  const [secretName, setSecretName] = useState("");
  const [secretValue, setSecretValue] = useState("");
  const [secretSaving, setSecretSaving] = useState(false);
  const [secretToDelete, setSecretToDelete] = useState(null); // name awaiting alert-dialog confirm

  useEffect(() => {
    api.get("/layout/templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!project?.id) return;
    api.get(`/projects/${project.id}/deploy-config`).then(({ data }) => {
      setProvider(data.provider || "local");
      setCfProjectName(data.cf_project_name || "");
      setCfCustomDomain(data.cf_custom_domain || "");
      setBuildDir(data.build_dir || "");
      setCloudflareConfigured(!!data.cloudflare_configured);
    }).catch(() => {});
    api.get(`/projects/${project.id}/deploys`).then(({ data }) => setDeploys(data.deploys || [])).catch(() => {});
    api.get(`/projects/${project.id}/secrets`).then(({ data }) => setSecretNames(data.names || [])).catch(() => {});
  }, [project?.id]);
  const dirs = templates.find((t) => t.key === template)?.dirs || [];
  const secretNameValid = SECRET_NAME_RE.test(secretName);

  async function saveSecret() {
    const name = secretName.trim();
    if (!SECRET_NAME_RE.test(name) || !secretValue) return;
    setSecretSaving(true);
    try {
      const { data } = await api.put(`/projects/${project.id}/secrets/${name}`, { value: secretValue }, { skipErrorToast: true });
      setSecretNames(data.names || []);
      setSecretName("");
      setSecretValue(""); // write-only: never keep the plaintext around after save
      toast.success(`Secret ${name} saved`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSecretSaving(false);
    }
  }

  async function deleteSecret(name) {
    try {
      const { data } = await api.delete(`/projects/${project.id}/secrets/${name}`, { skipErrorToast: true });
      setSecretNames(data.names || []);
      toast.success(`Secret ${name} deleted`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  }

  async function save() {
    setSaving(true);
    try {
      await api.put(`/projects/${project.id}/layout`, { template });
      // deploy-config replaces the whole deploy subdoc, so it must run BEFORE the
      // preview-config PUT (which dotted-$sets deploy.preview_command back on top).
      const deployCfg = { provider };
      if (provider === "cloudflare") {
        if (cloudflareApiToken) deployCfg.cloudflare_api_token = cloudflareApiToken;
        if (cloudflareAccountId) deployCfg.cloudflare_account_id = cloudflareAccountId;
        if (cfProjectName) deployCfg.cf_project_name = cfProjectName;
        if (cfCustomDomain) deployCfg.cf_custom_domain = cfCustomDomain;
      }
      if (buildDir) deployCfg.build_dir = buildDir;
      await api.post(`/projects/${project.id}/deploy-config`, deployCfg, { skipErrorToast: true });
      await api.put(`/projects/${project.id}/preview-config`, { preview_command: previewCommand });
      setCloudflareApiToken("");
      setCloudflareAccountId("");
      toast.success("Project settings saved");
      onSaved && onSaved({ template, preview_command: previewCommand });
      onClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div data-testid="project-settings-modal" className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-700 text-lg flex items-center gap-2"><Layers className="w-4.5 h-4.5" /> Project settings</h3>
          <button data-testid="project-settings-close" onClick={onClose} className="text-gray-400 hover:text-gray-900 p-1"><XIcon className="w-4 h-4" /></button>
        </div>

        <div className="space-y-5">
          <div>
            <label className="text-xs font-600 text-gray-500 mb-1.5 block">Project structure</label>
            <select data-testid="project-settings-layout" value={template} onChange={(e) => setTemplate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500">
              {templates.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            {dirs.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {dirs.map((d) => (
                  <span key={d.path} className="text-[11px] font-mono bg-gray-100 text-gray-600 rounded px-1.5 py-0.5" title={d.role}>{d.path}/</span>
                ))}
              </div>
            ) : <p className="text-[11px] text-gray-400 mt-1.5">Single tree — no enforced subdirectories.</p>}
            <p className="text-[11px] text-gray-400 mt-1.5">The planner organizes generated code into these folders and flags work that drifts outside them.</p>
          </div>

          <div>
            <label className="text-xs font-600 text-gray-500 mb-1.5 block flex items-center gap-1.5"><Terminal className="w-3.5 h-3.5" /> Live-preview dev command</label>
            <input data-testid="project-settings-preview-command" value={previewCommand} onChange={(e) => setPreviewCommand(e.target.value)}
              placeholder="auto-detect (e.g. npm run dev, uvicorn app:app --port {port})"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500" />
            <p className="text-[11px] text-gray-400 mt-1.5">Used by the "Live server" preview. Leave blank to auto-detect. Use <span className="font-mono">{"{port}"}</span> where your server's port goes; <span className="font-mono">PORT</span>/<span className="font-mono">HOST</span> env vars are also set.</p>
          </div>

          <details className="border border-gray-200 rounded-lg p-3 bg-gray-50/60">
            <summary data-testid="project-settings-hmr-help" className="text-xs font-600 text-gray-600 cursor-pointer select-none">
              Enable hot-reload (HMR) for Vite
            </summary>
            <p className="text-[11px] text-gray-500 mt-2">
              Cadence tunnels the dev server's HMR websocket through the preview. Point Vite's HMR client at Cadence by adding this to <span className="font-mono">vite.config.js</span> — the host injects the <span className="font-mono">CADENCE_PREVIEW_*</span> env vars at preview time:
            </p>
            <pre className="mt-2 bg-[#0A0A0A] text-gray-200 rounded-lg p-3 text-[10.5px] leading-relaxed font-mono overflow-x-auto cadence-scroll">{`const t = process.env.CADENCE_PREVIEW_TOKEN
export default {
  base: t ? \`/api/preview/\${t}/\` : '/',
  server: { hmr: t ? {
    protocol: 'wss',
    host: process.env.CADENCE_PREVIEW_HOST,
    clientPort: 443,
    path: '__cadence_hmr',
  } : true },
}`}</pre>
          </details>

          <div>
            <label className="text-xs font-600 text-gray-500 mb-1.5 block flex items-center gap-1.5">
              <Rocket className="w-3.5 h-3.5" /> Deploy target
              {cloudflareConfigured && (
                <span data-testid="deploy-cf-configured-badge" className="text-[10px] font-600 bg-emerald-50 text-emerald-700 rounded px-1.5 py-0.5">
                  Cloudflare connected
                </span>
              )}
            </label>
            <select data-testid="deploy-provider-select" value={provider} onChange={(e) => setProvider(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500">
              <option value="local">Local (simulated deploy)</option>
              <option value="cloudflare">Cloudflare Pages</option>
            </select>
            {provider === "cloudflare" && (
              <div className="mt-2 space-y-2">
                <input data-testid="deploy-cf-token" type="password" autoComplete="new-password" value={cloudflareApiToken}
                  onChange={(e) => setCloudflareApiToken(e.target.value)}
                  placeholder={cloudflareConfigured ? "API token (saved — enter a new one to replace)" : "Cloudflare API token"}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                <input data-testid="deploy-cf-account-id" value={cloudflareAccountId}
                  onChange={(e) => setCloudflareAccountId(e.target.value)}
                  placeholder={cloudflareConfigured ? "Account ID (saved — enter a new one to replace)" : "Cloudflare account ID"}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                <input data-testid="deploy-cf-project-name" value={cfProjectName}
                  onChange={(e) => setCfProjectName(e.target.value)} placeholder="Pages project name"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                <input data-testid="deploy-cf-custom-domain" value={cfCustomDomain}
                  onChange={(e) => setCfCustomDomain(e.target.value)} placeholder="Custom domain (optional, Pro)"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            )}
            <p className="text-[11px] text-gray-400 mt-1.5">Used by the Deploy button and Deployer chips. Credentials are stored encrypted and never shown again.</p>
          </div>

          <div>
            <label className="text-xs font-600 text-gray-500 mb-1.5 block">Deploy history</label>
            {deploys.length === 0 ? (
              <p data-testid="deploy-history-empty" className="text-[11px] text-gray-400">No deploys yet — hit Deploy in the workspace header to ship this project.</p>
            ) : (
              <div data-testid="deploy-history-list" className="space-y-1.5 max-h-44 overflow-y-auto cadence-scroll">
                {deploys.map((d) => (
                  <div key={d.id} data-testid="deploy-history-item" className="flex items-center gap-2 border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs">
                    <span className={`text-[10px] font-600 rounded px-1.5 py-0.5 ${d.status === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>{d.status}</span>
                    {d.url ? (
                      <a href={d.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate flex-1 font-mono">{d.url}</a>
                    ) : (
                      <span className="text-gray-400 truncate flex-1">no URL</span>
                    )}
                    <span className="font-mono text-gray-500 truncate max-w-[110px]">{d.branch}</span>
                    <span className="text-gray-400 whitespace-nowrap">{timeAgo(d.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-600 text-gray-500 mb-1.5 block flex items-center gap-1.5">
              <KeyRound className="w-3.5 h-3.5" /> Secrets
            </label>
            {secretNames.length === 0 ? (
              <p data-testid="secrets-empty" className="text-[11px] text-gray-400">No secrets yet — add one below.</p>
            ) : (
              <div data-testid="secrets-list" className="flex flex-wrap gap-1.5">
                {secretNames.map((n) => (
                  <span key={n} data-testid="secret-chip" className="inline-flex items-center gap-1 text-[11px] font-mono bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">
                    {n}
                    <button data-testid="secret-delete" aria-label={`Delete secret ${n}`} onClick={() => setSecretToDelete(n)}
                      className="text-gray-400 hover:text-red-600"><XIcon className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="mt-2 flex gap-2">
              <input data-testid="secret-name-input" value={secretName}
                onChange={(e) => setSecretName(e.target.value.toUpperCase())}
                placeholder="NAME (e.g. STRIPE_API_KEY)" spellCheck={false} autoComplete="off"
                className={`w-2/5 border rounded-lg px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500 ${secretName && !secretNameValid ? "border-red-300" : "border-gray-300"}`} />
              <input data-testid="secret-value-input" type="password" autoComplete="new-password" value={secretValue}
                onChange={(e) => setSecretValue(e.target.value)} placeholder="value (write-only)"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500" />
              <button data-testid="secret-save" onClick={saveSecret}
                disabled={secretSaving || !secretNameValid || !secretValue}
                className="bg-black text-white rounded-lg px-3 py-2 text-sm font-medium hover:bg-gray-900 disabled:opacity-40 whitespace-nowrap">
                {secretSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : secretNames.includes(secretName) ? "Update" : "Add"}
              </button>
            </div>
            {secretName && !secretNameValid && (
              <p data-testid="secret-name-error" className="text-[11px] text-red-500 mt-1">A–Z, 0–9 and _ only; must start with a letter (max 64 chars).</p>
            )}
            <p className="text-[11px] text-gray-400 mt-1.5">Values are write-only and encrypted at rest; they're injected into previews and deploys as environment variables.</p>
          </div>
        </div>

        <AlertDialog open={!!secretToDelete} onOpenChange={(open) => { if (!open) setSecretToDelete(null); }}>
          <AlertDialogContent data-testid="secret-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete secret {secretToDelete}?</AlertDialogTitle>
              <AlertDialogDescription>Previews and deploys will stop receiving it.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="secret-delete-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="secret-delete-do"
                onClick={() => { const n = secretToDelete; setSecretToDelete(null); if (n) deleteSecret(n); }}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <button data-testid="project-settings-save" onClick={save} disabled={saving}
          className="mt-6 w-full bg-black text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-900 disabled:opacity-60 inline-flex items-center justify-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save settings"}
        </button>
      </div>
    </div>
  );
}
